import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  Paper,
  Grid,
  Fade,
  LinearProgress,
  Modal,
  MenuItem,
  FormControl,
  TextField,
  InputLabel,
  Button,
  Select,
} from '@material-ui/core';
import { NotificationManager } from 'react-notifications';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import MUIDataTable from 'mui-datatables';

import PageTitle from '../components/PageTitle/PageTitle';
import useFirestore from '../hooks/useFirestore';
import useUser from '../hooks/useUser';

const useStyles = makeStyles((theme) => ({
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paper: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(4),
  },
  termModal: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 700,

    '& .MuiFormControl-root': {
      width: '100%',
    },

    '& .actions': {
      marginTop: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      '& button:first-child': {
        marginRight: 16,
      },
    },

    '& .edit-textbox': {
      padding: 10,
      marginTop: 16,
      border: '1px solid #F1F1F1',
    },

    '& .rdw-editor-wrapper': {
      border: '1px solid #4A4A4A',

      '& .rdw-editor-main': {
        borderTop: '1px solid #4A4A4A',
      },
    },
  },
}));

const categories = [
  {
    value: 1,
    label: 'Terms to Know',
  },
  {
    value: 2,
    label: 'Terms with Multiple Meanings',
  },
  {
    value: 3,
    label: 'Terms to Avoid',
  },
];

const categoriesString = ['', 'Terms to Know', 'Terms with Multiple Meanings', 'Terms to Avoid'];

function Terms() {
  const classes = useStyles();
  const firestore = useFirestore();

  const [user] = useUser();

  const [isLoading, setLoading] = useState(false);
  const [termsData, setTermsData] = useState([]);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowEditModal, setIsShowEditModal] = useState(false);
  const [newTerm, setNewTerm] = useState({
    title: '',
    category_id: '',
    submitted_by: '',
    aka: '',
    description: '',
    status: 'Draft',
  });
  const [editTerm, setEditTerm] = useState({});

  const handleClose = () => {
    setIsShowModal(false);
  };

  const handleEditClose = () => {
    setIsShowEditModal(false);
  };

  const handleChange = (event) => {
    setNewTerm({ ...newTerm, [event.target.name]: event.target.value });
  };

  const handleUpdateChange = (event) => {
    setEditTerm({ ...editTerm, [event.target.name]: event.target.value });
  };

  const columns = [
    {
      name: 'id',
      label: 'Id',
      options: {
        display: false,
        filter: false,
        sort: false,
      },
    },
    {
      name: 'title',
      label: 'Title',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'submitted_by',
      label: 'Submitted By',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'aka',
      label: 'Also Known As',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'category',
      label: 'Category',
      options: {
        filter: true,
        sort: true,
      },
    },
    // {
    //   name: 'suggested_use',
    //   label: 'Suggested Use',
    //   options: {
    //     filter: true,
    //     sort: true,
    //   },
    // },
    {
      name: 'description',
      label: 'Description',
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'Edit',
      options: {
        filter: true,
        sort: false,
        empty: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => handleEdit(tableMeta.tableData[tableMeta.rowIndex][0])}
            >
              Edit
            </Button>
          );
        },
      },
    },
  ];

  useEffect(() => {
    const fn = async () => {
      setLoading(true);
      const result = await firestore.collection('terms').get();

      if (!result.empty) {
        const data = await result.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
        console.log('data', data);
        setTermsData(
          data.map((t) => {
            return { ...t, category: categoriesString[t.category_id] };
          }),
        );
      } else {
        setTermsData([]);
      }
      setLoading(false);
    };

    if (user) fn();
  }, [firestore, user]);

  const handleEdit = (id) => {
    setEditTerm(termsData.find((t) => t.id === id));
    console.log(editTerm);
    setIsShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('add new Term', newTerm);
    if (newTerm.title !== '' && newTerm.submitted_by !== '') {
      const created = await firestore
        .collection('terms')
        .add({ ...newTerm, created_at: new Date() });
      console.log(created);
      NotificationManager.success('Term Create Success');

      setTermsData([...termsData, { ...newTerm, id: created.id }]);
    }
    setIsShowModal(false);
    return false;
  };

  const handleUpdateTerm = async (e) => {
    e.preventDefault();
    console.log('Update Term', editTerm);
    delete editTerm.created_at;
    delete editTerm.category;
    if (editTerm.title !== '' && editTerm.submitted_by !== '') {
      await firestore
        .collection('terms')
        .doc(editTerm.id)
        .update({ ...editTerm, updated_at: new Date() });

      NotificationManager.success('Term Update Success');

      setTermsData(
        termsData.map((t) => {
          if (t.id === editTerm.id) {
            return editTerm;
          }
          return t;
        }),
      );
      //onRowsDelete
    }
    setIsShowEditModal(false);
    return false;
  };

  return (
    <>
      <Fade in={isLoading}>
        <LinearProgress variant="query" />
      </Fade>
      <div className={classes.titleContainer}>
        <PageTitle title="Terms" />
      </div>
      <Paper className={classes.paper}>
        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={setIsShowModal}>
            Create New Term
          </Button>
        </Grid>
      </Paper>
      {termsData && (
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MUIDataTable
                title="Terms"
                data={termsData}
                columns={columns}
                options={{
                  filterType: 'checkbox',
                  print: false,
                  download: false,
                  onRowsDelete: (data) => {
                    console.log(data);
                  },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}
      <Modal
        open={isShowModal}
        onClose={handleClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <div className={classes.termModal}>
          <form action="#" onSubmit={handleSubmit}>
            <h2>Create a new term!</h2>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  id="title"
                  label="Title"
                  variant="outlined"
                  name="title"
                  value={newTerm.title}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  id="submitted_by"
                  label="Submitted By"
                  variant="outlined"
                  name="submitted_by"
                  value={newTerm.submitted_by}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  id="aka"
                  label="Also Known As"
                  variant="outlined"
                  name="aka"
                  value={newTerm.aka}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category_id-label"
                    id="category_id"
                    name="category_id"
                    value={newTerm.category_id}
                    onChange={handleChange}
                    label="Category"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {categories.map((item) => (
                      <MenuItem key={'cat' + item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* <Grid item xs={12}>
              <TextField
                required
                id="suggested_use"
                label="Suggested Use"
                variant="outlined"
                name="suggested_use"
                value={newTerm.suggested_use}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid> */}
              <Grid item xs={12}>
                <TextField
                  required
                  id="description"
                  label="Description"
                  variant="outlined"
                  name="description"
                  value={newTerm.description}
                  onChange={handleChange}
                  multiline
                  rows={6}
                />
                {/* <Editor
                defaultContentState={contentState}
                onContentStateChange={setContentState}
                editorClassName="edit-textbox"
                name="description"
              /> */}
              </Grid>
              <Grid item xs={12} md={12}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    id="status"
                    name="status"
                    value={newTerm.status}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value="Draft">Draft</MenuItem>
                    <MenuItem value="Published">Published</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <div className="actions">
              <Button variant="contained" color="primary" type="submit">
                Create
              </Button>
              <Button variant="outlined" color="primary" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        open={isShowEditModal}
        onClose={handleEditClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <div className={classes.termModal}>
          <form action="#" onSubmit={handleUpdateTerm}>
            <h2>Edit the term!</h2>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  id="title"
                  label="Title"
                  variant="outlined"
                  name="title"
                  value={editTerm.title}
                  onChange={handleUpdateChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  id="submitted_by"
                  label="Submitted By"
                  variant="outlined"
                  name="submitted_by"
                  value={editTerm.submitted_by}
                  onChange={handleUpdateChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  id="aka"
                  label="Also Known As"
                  variant="outlined"
                  name="aka"
                  value={editTerm.aka}
                  onChange={handleUpdateChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category_id-label"
                    id="category_id"
                    name="category_id"
                    value={editTerm.category_id}
                    onChange={handleUpdateChange}
                    label="Category"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {categories.map((item) => (
                      <MenuItem key={'cat' + item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* <Grid item xs={12}>
              <TextField
                required
                id="suggested_use"
                label="Suggested Use"
                variant="outlined"
                name="suggested_use"
                value={editTerm.suggested_use}
                onChange={handleUpdateChange}
                multiline
                rows={2}
              />
            </Grid> */}
              <Grid item xs={12}>
                <TextField
                  required
                  id="description"
                  label="Description"
                  variant="outlined"
                  name="description"
                  value={editTerm.description}
                  onChange={handleUpdateChange}
                  multiline
                  rows={6}
                />
                {/* <Editor
                defaultContentState={contentState}
                onContentStateChange={setContentState}
                editorClassName="edit-textbox"
                name="description"
              /> */}
              </Grid>
              <Grid item xs={12} md={12}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    id="status"
                    name="status"
                    value={editTerm.status}
                    onChange={handleUpdateChange}
                    label="Status"
                  >
                    <MenuItem value="Draft">Draft</MenuItem>
                    <MenuItem value="Published">Published</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <div className="actions">
              <Button variant="contained" color="primary" type="submit">
                Update
              </Button>
              <Button variant="outlined" color="primary" onClick={handleEditClose}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}

export default Terms;
